import { useState, useRef, useCallback, useEffect } from "react";
import { Mic, Square, Play, Pause, Trash2, Loader2, Volume2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Services displayed with friendly names only — no CPT codes or prices shown to provider
const SERVICE_OPTIONS = [
  { id: "office-visit-brief", label: "Brief Office Visit", cptCode: "99212", category: "Office Visits" },
  { id: "office-visit-standard", label: "Standard Office Visit", cptCode: "99213", category: "Office Visits" },
  { id: "office-visit-extended", label: "Extended Office Visit", cptCode: "99214", category: "Office Visits" },
  { id: "office-visit-comprehensive", label: "Comprehensive Office Visit", cptCode: "99215", category: "Office Visits" },
  { id: "new-patient-eval", label: "New Patient Evaluation", cptCode: "99203", category: "Evaluations" },
  { id: "new-patient-eval-complex", label: "Complex New Patient Evaluation", cptCode: "99205", category: "Evaluations" },
  { id: "telehealth-brief", label: "Brief Telehealth Session", cptCode: "99441", category: "Telehealth" },
  { id: "telehealth-standard", label: "Standard Telehealth Session", cptCode: "99442", category: "Telehealth" },
  { id: "telehealth-extended", label: "Extended Telehealth Session", cptCode: "99443", category: "Telehealth" },
  { id: "psychotherapy-30", label: "Individual Counseling (30 min)", cptCode: "90832", category: "Behavioral Health" },
  { id: "psychotherapy-45", label: "Individual Counseling (45 min)", cptCode: "90834", category: "Behavioral Health" },
  { id: "psychotherapy-60", label: "Individual Counseling (60 min)", cptCode: "90837", category: "Behavioral Health" },
  { id: "psych-eval", label: "Psychiatric Evaluation", cptCode: "90791", category: "Behavioral Health" },
  { id: "family-therapy", label: "Family Therapy", cptCode: "90847", category: "Behavioral Health" },
  { id: "group-therapy", label: "Group Therapy", cptCode: "90853", category: "Behavioral Health" },
  { id: "rpm-setup", label: "Remote Monitoring Setup", cptCode: "99453", category: "Remote Monitoring" },
  { id: "rpm-device-supply", label: "Remote Monitoring Device Supply", cptCode: "99454", category: "Remote Monitoring" },
  { id: "rpm-management", label: "Remote Monitoring Management", cptCode: "99457", category: "Remote Monitoring" },
  { id: "care-coordination", label: "Care Coordination", cptCode: "99487", category: "Care Management" },
  { id: "chronic-care-mgmt", label: "Chronic Care Management", cptCode: "99490", category: "Care Management" },
  { id: "preventive-visit", label: "Annual Wellness Visit", cptCode: "99385", category: "Preventive" },
];

// Group services by category
const servicesByCategory = SERVICE_OPTIONS.reduce<Record<string, typeof SERVICE_OPTIONS>>((acc, svc) => {
  if (!acc[svc.category]) acc[svc.category] = [];
  acc[svc.category].push(svc);
  return acc;
}, {});

interface PatientNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientName: string;
  onNoteSaved: () => void;
}

export function PatientNotesDialog({ open, onOpenChange, patientId, patientName, onNoteSaved }: PatientNotesDialogProps) {
  const { user } = useAuth();
  const [noteText, setNoteText] = useState("");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [volume, setVolume] = useState(80);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("default");
  const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState<string>("default");
  const [liveTranscript, setLiveTranscript] = useState("");
  const [isTranscribing, setIsTranscribing] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recognitionRef = useRef<any>(null);

  // Enumerate audio input devices
  useEffect(() => {
    if (!open) return;
    const loadDevices = async () => {
      try {
        // Request permission first so labels are populated
        await navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => s.getTracks().forEach((t) => t.stop()));
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter((d) => d.kind === "audioinput");
        const audioOutputs = devices.filter((d) => d.kind === "audiooutput");
        setAudioDevices(audioInputs);
        setOutputDevices(audioOutputs);
        if (audioInputs.length > 0 && selectedDeviceId === "default") {
          setSelectedDeviceId(audioInputs[0].deviceId);
        }
        if (audioOutputs.length > 0 && selectedOutputDeviceId === "default") {
          setSelectedOutputDeviceId(audioOutputs[0].deviceId);
        }
      } catch {
        // Permission denied — will handle on record attempt
      }
    };
    loadDevices();
  }, [open]);

  // Update audio volume when slider changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  // Set output device on audio element when it changes
  useEffect(() => {
    if (audioRef.current && selectedOutputDeviceId && typeof (audioRef.current as any).setSinkId === "function") {
      (audioRef.current as any).setSinkId(selectedOutputDeviceId).catch(() => {
        // setSinkId not supported or failed silently
      });
    }
  }, [selectedOutputDeviceId, audioUrl]);

  const startSpeechRecognition = useCallback(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.info("Speech-to-text is not supported in this browser. You can still record audio.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    let finalTranscript = "";

    recognition.onresult = (event: any) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + " ";
          // Append finalized text to the note
          setNoteText((prev) => {
            const separator = prev && !prev.endsWith(" ") && !prev.endsWith("\n") ? " " : "";
            return prev + separator + transcript;
          });
        } else {
          interim += transcript;
        }
      }
      setLiveTranscript(interim);
    };

    recognition.onerror = (event: any) => {
      if (event.error !== "aborted") {
        console.warn("Speech recognition error:", event.error);
      }
    };

    recognition.onend = () => {
      setIsTranscribing(false);
      setLiveTranscript("");
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsTranscribing(true);
  }, []);

  const stopSpeechRecognition = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsTranscribing(false);
    setLiveTranscript("");
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined },
      });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingDuration(0);
      timerRef.current = setInterval(() => setRecordingDuration((d) => d + 1), 1000);

      // Start live transcription
      startSpeechRecognition();
    } catch {
      toast.error("Could not access microphone. Please grant permission.");
    }
  }, [selectedDeviceId, startSpeechRecognition]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    setIsPaused(false);
    if (timerRef.current) clearInterval(timerRef.current);
    stopSpeechRecognition();
  }, [stopSpeechRecognition]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) clearInterval(timerRef.current);
      stopSpeechRecognition();
    }
  }, [stopSpeechRecognition]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => setRecordingDuration((d) => d + 1), 1000);
      startSpeechRecognition();
    }
  }, [startSpeechRecognition]);

  const deleteRecording = () => {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setRecordingDuration(0);
    setIsPlaying(false);
  };

  const togglePlayback = () => {
    if (!audioRef.current || !audioUrl) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.volume = volume / 100;
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleService = (serviceId: string) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter((s) => s !== serviceId) : [...prev, serviceId]
    );
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleSave = async () => {
    if (!noteText.trim() && !audioBlob) {
      toast.error("Please enter a note or record a voice memo.");
      return;
    }
    if (selectedServices.length === 0) {
      toast.error("Please select at least one service provided.");
      return;
    }
    if (!user) return;

    setIsSaving(true);
    try {
      let voiceMemoUrl: string | null = null;

      if (audioBlob) {
        const fileName = `${user.id}/${patientId}/${Date.now()}.webm`;
        const { error: uploadError } = await supabase.storage
          .from("voice-memos")
          .upload(fileName, audioBlob, { contentType: "audio/webm" });

        if (uploadError) throw uploadError;
        voiceMemoUrl = fileName;
      }

      const selectedCptCodes = selectedServices
        .map((id) => SERVICE_OPTIONS.find((s) => s.id === id)?.cptCode)
        .filter(Boolean) as string[];

      const servicesData = selectedServices.map((id) => {
        const svc = SERVICE_OPTIONS.find((s) => s.id === id)!;
        return { id: svc.id, label: svc.label, category: svc.category };
      });

      const { error: noteError } = await supabase
        .from("patient_notes")
        .insert({
          patient_id: patientId,
          provider_id: user.id,
          note_text: noteText.trim() || null,
          voice_memo_url: voiceMemoUrl,
          voice_memo_duration_seconds: audioBlob ? recordingDuration : null,
          services: servicesData,
          cpt_codes: selectedCptCodes,
          billing_synced: false,
        });

      if (noteError) throw noteError;

      toast.success("Note saved successfully. Services will sync to billing.");
      resetForm();
      onNoteSaved();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(`Failed to save note: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setNoteText("");
    setSelectedServices([]);
    deleteRecording();
    setLiveTranscript("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stopSpeechRecognition(); resetForm(); } onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Patient Note</DialogTitle>
          <DialogDescription>
            Document encounter notes for {patientName}. Select services provided below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Clinical Notes with live transcription */}
          <div className="space-y-2">
            <Label>Clinical Notes</Label>
            <Textarea
              placeholder="Enter clinical notes, or press Record to dictate..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="min-h-[120px]"
            />
            {/* Live transcription indicator */}
            {isTranscribing && liveTranscript && (
              <div className="rounded-md bg-muted/50 border border-border p-2">
                <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                  </span>
                  Listening...
                </p>
                <p className="text-sm text-muted-foreground italic">{liveTranscript}</p>
              </div>
            )}
            {isTranscribing && !liveTranscript && (
              <div className="rounded-md bg-muted/50 border border-border p-2">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                  </span>
                  Listening for speech... speak now
                </p>
              </div>
            )}
          </div>

          {/* Voice Memo */}
          <div className="space-y-3">
            <Label>Voice Memo</Label>

            {/* Microphone Selection */}
            {audioDevices.length > 1 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Microphone Source</Label>
                <Select value={selectedDeviceId} onValueChange={setSelectedDeviceId} disabled={isRecording}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select microphone" />
                  </SelectTrigger>
                  <SelectContent>
                    {audioDevices.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex flex-col gap-3 p-3 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center gap-3">
                {!audioBlob && !isRecording && (
                  <Button variant="outline" size="sm" onClick={startRecording}>
                    <Mic className="h-4 w-4 mr-2" />
                    Record Voice Memo
                  </Button>
                )}

                {isRecording && (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-3 w-3">
                        {!isPaused && (
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                        )}
                        <span className={`relative inline-flex rounded-full h-3 w-3 ${isPaused ? 'bg-amber-500' : 'bg-destructive'}`} />
                      </span>
                      <span className={`text-sm font-medium ${isPaused ? 'text-amber-600' : 'text-destructive'}`}>
                        {isPaused ? "Paused" : "Recording"}
                      </span>
                      <span className="text-sm text-muted-foreground font-mono">{formatDuration(recordingDuration)}</span>
                    </div>
                    {isPaused ? (
                      <Button variant="outline" size="sm" onClick={resumeRecording}>
                        <Play className="h-4 w-4 mr-1" />
                        Resume
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={pauseRecording}>
                        <Pause className="h-4 w-4 mr-1" />
                        Pause
                      </Button>
                    )}
                    <Button variant="destructive" size="sm" onClick={stopRecording}>
                      <Square className="h-4 w-4 mr-1" />
                      Stop
                    </Button>
                  </>
                )}

                {audioBlob && !isRecording && (
                  <>
                    <audio
                      ref={audioRef}
                      src={audioUrl || ""}
                      onEnded={() => setIsPlaying(false)}
                    />
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={togglePlayback}>
                      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm text-muted-foreground font-mono">
                      {formatDuration(recordingDuration)}
                    </span>
                    <Badge variant="secondary">Voice memo attached</Badge>
                    <Button variant="ghost" size="icon" className="h-8 w-8 ml-auto text-destructive" onClick={deleteRecording}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>

              {/* Volume Control — visible when a recording exists */}
              {audioBlob && !isRecording && (
                <div className="space-y-2">
                  {/* Output Device Selection */}
                  {outputDevices.length > 1 && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Output Sound Source</Label>
                      <Select value={selectedOutputDeviceId} onValueChange={setSelectedOutputDeviceId}>
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue placeholder="Select speaker" />
                        </SelectTrigger>
                        <SelectContent>
                          {outputDevices.map((device) => (
                            <SelectItem key={device.deviceId} value={device.deviceId}>
                              {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Volume Control */}
                  <div className="flex items-center gap-3">
                    <Volume2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Slider
                      value={[volume]}
                      onValueChange={(v) => setVolume(v[0])}
                      max={100}
                      min={0}
                      step={1}
                      className="flex-1"
                    />
                    <span className="text-xs text-muted-foreground w-8 text-right">{volume}%</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Service Selection */}
          <div className="space-y-3">
            <Label>Services Provided *</Label>
            <p className="text-sm text-muted-foreground">
              Select all services rendered during this encounter
            </p>
            <div className="space-y-4 max-h-[250px] overflow-y-auto rounded-lg border border-border p-3">
              {Object.entries(servicesByCategory).map(([category, services]) => (
                <div key={category}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    {category}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {services.map((svc) => (
                      <label
                        key={svc.id}
                        className="flex items-center gap-2 rounded-md px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
                      >
                        <Checkbox
                          checked={selectedServices.includes(svc.id)}
                          onCheckedChange={() => toggleService(svc.id)}
                        />
                        <span className="text-sm">{svc.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {selectedServices.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedServices.map((id) => {
                  const svc = SERVICE_OPTIONS.find((s) => s.id === id)!;
                  return (
                    <Badge
                      key={id}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => toggleService(id)}
                    >
                      {svc.label} ×
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { stopSpeechRecognition(); resetForm(); onOpenChange(false); }}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
